<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/solid';
import type { PropType } from 'vue';
import type { NftCollectionWithAttributeFrequencies } from '~/types/generated';

const props = defineProps({
  modelValue: { required: true, type: Object as PropType<{ name: string; value: string }[]> },
  collection: { required: true, type: Object as PropType<NftCollectionWithAttributeFrequencies> },
});
const { modelValue: attributeFilters, collection } = toRefs(props);
const emit = defineEmits(['update:modelValue']);

const attributes = computed(() => {
  return (collection.value?.attributes || []).map(({ name, values }) => ({
    name,
    values: values
      .map((value) => ({
        value,
        count: collection.value.attributes_frequency_counts
          ? getAttributeCount(collection.value.attributes_frequency_counts, name, value)
          : undefined,
      }))
      .sort(({ count: a }, { count: b }) => b - a),
  }));
});

const addFilter = (name: string, value: string) => {
  const existingFilterWithNameIdx = attributeFilters.value.findIndex((filter) => filter.name === name);
  if (existingFilterWithNameIdx >= 0) {
    const existingFilterWithName = attributeFilters.value[existingFilterWithNameIdx];
    if (existingFilterWithName.value === value) {
      attributeFilters.value.splice(existingFilterWithNameIdx, 1);
    } else {
      existingFilterWithName.value = value;
    }
  } else {
    attributeFilters.value.push({ name, value });
  }
  emit('update:modelValue', attributeFilters.value);
};

const getAttributeCount = (attributesFrequencyCounts: any, name: string, value: string) => {
  name = name.toLowerCase();
  value = String(value).toLowerCase();
  return attributesFrequencyCounts[name]?.[value];
};
</script>
<template>
  <aside>
    <!--            <h2 class="sr-only">Filters</h2>-->
    <!--            <button type="button" class="inline-flex items-center lg:hidden" @click="mobileFiltersOpen = true">-->
    <!--              <span class="text-sm font-medium text-neutral-700 dark:text-neutral-200">Filters</span>-->
    <!--              <PlusSmIcon class="ml-1 h-5 w-5 flex-shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />-->
    <!--            </button>-->
    <div class="hidden w-60 rounded-lg border p-4 dark:border-0 dark:border-neutral-700 dark:bg-neutral-800 lg:block">
      <!-- Side-menu filters -->
      <form class="space-y-1" @submit.prevent="">
        <div v-for="attribute in attributes" :key="attribute.name">
          <Disclosure v-slot="{ open }" as="div">
            <fieldset>
              <legend class="w-full">
                <DisclosureButton
                  class="flex w-full items-center justify-between py-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-500"
                >
                  <span
                    class="break-words-properly text-start text-sm text-neutral-900 dark:text-neutral-50"
                    :class="attributeFilters.find((a) => a.name === attribute.name) ? 'font-bold' : 'font-medium'"
                  >
                    {{ attribute.name }}
                  </span>
                  <span class="ml-6 flex h-7 items-center">
                    <ChevronDownIcon
                      :class="[open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform']"
                      aria-hidden="true"
                    />
                  </span>
                </DisclosureButton>
              </legend>
              <DisclosurePanel>
                <div class="space-y-3 pb-6 pt-2">
                  <div v-for="{ value, count } in attribute.values" :key="value" class="flex items-center gap-1">
                    <label
                      :for="`attribute-${attribute.name}-${value}`"
                      class="break-words-properly flex-1 text-sm text-neutral-600 dark:text-neutral-300"
                    >
                      {{ value }}
                      <template v-if="count"> ({{ count }})</template>
                    </label>
                    <input
                      :id="`attribute-${attribute.name}-${value}`"
                      :value="value || 'None'"
                      :name="`${attribute.name}[]`"
                      :checked="!!attributeFilters.find((a) => a.name === attribute.name && a.value === value)"
                      type="radio"
                      class="h-4 w-4 rounded border border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:checked:bg-neutral-800"
                      @click="addFilter(attribute.name, value)"
                    />
                  </div>
                </div>
              </DisclosurePanel>
            </fieldset>
          </Disclosure>
        </div>
      </form>
    </div>
  </aside>
</template>
